// components/chatbot/chatbot-widget.tsx
import {
  ChatMessage,
  useChatbotSession,
} from "@/src/hooks/features/chatbot/use-chatbot-session";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle, MessageText, Send } from "iconsax-react-nativejs";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

type ChatbotWidgetProps = Readonly<{
  widgetId: string;
  agentName?: string;
}>;

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === "user";
  return (
    <View
      className={`flex-row mb-3 ${isUser ? "justify-end" : "justify-start"}`}
    >
      <View
        className={`max-w-[80%] px-4 py-3 rounded-2xl ${
          isUser ? "bg-primary rounded-tr-sm" : "bg-ash-3 rounded-tl-sm"
        }`}
      >
        <Text className={`text-sm ${isUser ? "text-white" : "text-onyx"}`}>
          {message.content}
          {message.isStreaming && <ActivityIndicator />}
        </Text>
      </View>
    </View>
  );
};

const ChatbotWidget = ({
  widgetId,
  agentName = "Assistant",
}: ChatbotWidgetProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const flatListRef = useRef<FlatList>(null);
  const { messages, isLoading, sendMessage, clearMessages } =
    useChatbotSession(widgetId);
  const RIGHT_INSET = 24; // matches the container's `right-6`
  const SIDE_MARGIN = 32;
  const panelWidth = Math.min(280, screenWidth - RIGHT_INSET - SIDE_MARGIN);
  const panelHeight = Math.min(400, Math.round(screenHeight * 0.5));

  const handleSend = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    await sendMessage(text);
    flatListRef.current?.scrollToEnd({ animated: true });
  };

  return (
    <View className="absolute bottom-6 right-6 z-50">
      {/* Chat window */}
      {isOpen && (
        <View
          className="absolute bottom-16 right-0 bg-chalk rounded-2xl border border-ash-3 overflow-hidden"
          style={{
            width: panelWidth,
            height: panelHeight,
            maxWidth: screenWidth - RIGHT_INSET - SIDE_MARGIN,
            maxHeight: screenHeight - 200,
            shadowOpacity: 0.15,
            shadowRadius: 10,
          }}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-4 py-3 bg-primary">
            <Text className="text-white font-semibold text-base">
              {agentName.split("-").at(-1)}
            </Text>
            {/* items-center, or React Native's default `alignItems: stretch`
                stretches Clear to the height of the taller close button and its
                text-xs label sits at the top of that box — reading as if Clear
                is floating above the icon rather than beside it. */}
            <View className="flex-row items-center gap-3">
              <Pressable
                onPress={clearMessages}
                className="hover:bg-ash-3 hover:rounded-lg"
              >
                <Text className="text-primary-light text-xs">
                  {t("BTN_CLEAR")}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setIsOpen(false)}
                className="p-1.5 rounded-full hover:bg-ash-3"
              >
                <CloseCircle size={20} color="white" />
              </Pressable>
            </View>
          </View>

          {/* Messages */}
          <FlatList
            ref={flatListRef}
            data={messages}
            className="flex-1 bg-chalk"
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => <MessageBubble message={item} />}
            contentContainerStyle={{ padding: 16, flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            onContentSizeChange={() =>
              flatListRef.current?.scrollToEnd({ animated: true })
            }
            ListEmptyComponent={
              <View className="flex-1 items-center justify-center py-10">
                <Text className="text-ash-1 text-sm text-center">
                  {t("LABEL_CHATBOT_GREETING")}
                </Text>
              </View>
            }
          />

          {/* Input */}
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : undefined}
          >
            <View className="flex-row items-center gap-2 px-3 py-2 border-t border-ash-3 bg-chalk">
              <TextInput
                value={input}
                onChangeText={setInput}
                placeholder={t("LABEL_TYPE_MESSAGE")}
                placeholderTextColor={themeVars["--color-ash-2"]}
                className="flex-1 text-sm text-onyx px-3 py-2 rounded-xl bg-ash"
                // Enter sends, Shift+Enter makes a new line.
                //
                // `onSubmitEditing` never fired here. react-native-web only calls
                // it when `(blurOnSubmit || !multiline)`, and this input is
                // multiline with blurOnSubmit false, so Enter just inserted a
                // newline. Raising blurOnSubmit would fix that but also blurs the
                // field after every send, so the key is handled directly.
                //
                // preventDefault stops the newline and also marks the event
                // default-prevented, so RNW skips its own submit path. No double
                // send. IME composition is skipped, or Enter would send a
                // half-composed word.
                onKeyPress={(event) => {
                  const key = event.nativeEvent as unknown as {
                    key?: string;
                    shiftKey?: boolean;
                    isComposing?: boolean;
                  };
                  if (key.key !== "Enter" || key.shiftKey || key.isComposing) {
                    return;
                  }
                  event.preventDefault?.();
                  handleSend();
                }}
                onSubmitEditing={handleSend}
                returnKeyType="send"
                blurOnSubmit={false}
                multiline
                style={{ maxHeight: 80, outlineStyle: "none" } as any}
              />
              {isLoading ? (
                <ActivityIndicator
                  size="small"
                  color={themeVars["--color-primary-default"]}
                />
              ) : (
                <Pressable
                  onPress={handleSend}
                  disabled={!input.trim()}
                  className="p-2 rounded-full bg-primary hover:opacity-90"
                  style={{ opacity: input.trim() ? 1 : 0.5 }}
                >
                  <Send size={18} color="white" />
                </Pressable>
              )}
            </View>
          </KeyboardAvoidingView>
        </View>
      )}

      {/* Floating button */}
      <Pressable
        onPress={() => setIsOpen((prev) => !prev)}
        className="w-14 h-14 rounded-full bg-primary items-center justify-center hover:opacity-90"
        style={{ shadowOpacity: 0.3, shadowRadius: 8 }}
      >
        {isOpen ? (
          <CloseCircle size={28} color="white" />
        ) : (
          <MessageText size={28} color="white" />
        )}
      </Pressable>
    </View>
  );
};

export default ChatbotWidget;
